/**
 * README!!!
 * last updated: TD 4/25/22
 *
 * Questionnaire Engine authentication workflow:
 *  Questionnaire engine makes use of an iframe to render the selected form using the old
 *  C2 backend server.  Since we are using an iframe, and since we do not want to rely on
 *  the C2 server generating its own keycloak instance, keycloak state management is done
 *  in CTM code and the updated token is passed to the clinical iframe for use.
 *
 *  There does not seem to be a way to pass the keycloak object directly to the iframe due
 *  to it containing functions.  Therefor only the updated token is passed.
 *
 *  On load, a useEffect function is called that creates a keycloak callback
 *  onAuthRefreshSuccess. This function is triggered when ever a successful
 *  keycloak.updateToken(tokenExpirytime)call is made.  the function then posts the
 *  updated token via message to the iframe for the iframe to consume.  The
 *  tokenExpirytime variable is passed to the updateToken call so that the call will only
 *  fire if it is within the threshold time for the timeout.
 *
 *
 */

/* eslint-disable no-unused-expressions */
/* eslint-disable no-nested-ternary */
import React, { useState, useContext, useEffect, useRef } from 'react';
import { useTheme } from '@material-ui/core/styles';
import moment from 'moment-timezone';
import { Loader } from '@mummssoftware/common-ui';
import {
  GlobalContext,
  ADD_FORM,
} from '@mummssoftware/global-context/GlobalContext';
import { formatPostDate } from '@mummssoftware/utils/formatters';
import { VisitsGlobalContext } from '@mummssoftware/global-context';
import {
  ClinicalPatientObject,
  ClinicalQuestionnaire,
  FormsContext,
  PatientState,
} from '../../context';
import defaults from '../../translations/en.json';
import {
  showSaveWarning,
  showSaveWarningDialog,
} from './QuestionnaireEngineSaveWarning';
import { DEFAULT_STRING } from './constants';
import { questionnaireEngine } from '../Form/constants';
import { FormWidgetWrapper } from '../../components/layout';

type QuestionnaireEngineProps = {
  formName: string;
  topBarHeight?: number;
  showSpinner?: boolean;
  hideCheckmark?: boolean;
  setHideCheckmark?: (value: boolean) => void;
};

const translations: NonNullable<mumms.JSONObject> = defaults;

export const QuestionnaireEngine: React.FC<QuestionnaireEngineProps> = ({
  formName,
  topBarHeight,
  showSpinner,
  hideCheckmark,
  setHideCheckmark,
}: QuestionnaireEngineProps) => {
  const [questionnaireHtml, setQuestionnaireHtml] = useState<
    string | undefined
  >(undefined);
  const [engineStarted, startEngine] = useState<boolean>(false);
  const [showFormsLocal, setShowFormslocal] = useState<boolean>(true);
  const [couchPouchErrorMsg, setCouchPouchErrorMsg] = useState<any>({});
  const cpErrMsgRef = useRef(couchPouchErrorMsg);

  const {
    onClick,
    patient,
    smallWidget,
    currentUser,
    keycloak,
    observation,
    database,
    showForms,
    setShowForms,
    siteId,
    updateFormsPatientState,
  } = useContext(FormsContext);

  const tokenExpirytime: number = parseInt(
    (process.env as Record<string, string>).REACT_APP_TOKEN_VALIDITY_IN_SECONDS,
  );

  const { appVisit, dispatch } = useContext(GlobalContext);
  const { providerInfo } = useContext(VisitsGlobalContext);

  const {
    props: {
      dashboard: { smallWidgetHeight, getFullSizeHeight },
    },
  } = useTheme() as any;

  const fullSizeHeight = getFullSizeHeight(window.innerHeight);

  const height = smallWidget ? smallWidgetHeight : (fullSizeHeight as number);

  let iframeWindow: HTMLIFrameElement | null = null; // initialize this to a blank element.
  const iframeWindowRef = useRef<HTMLIFrameElement | null>(iframeWindow);
  iframeWindowRef.current = iframeWindow;
  const firm = currentUser.hbcFirm;
  // TODO set this back once the currentUser call starts returning the correct site id
  // currentUser.siteId is only returning the default site id and not the current
  // site id the user is in.
  // const currentSite = currentUser?.siteList?.filter(
  //   (s) => s.id === currentUser.siteId,
  // )[0];
  const location =
    currentUser?.siteList?.filter((s) => `${s.id}` === siteId)[0]
      ?.hbcLocation || '';

  const attemptFetch = async (url: string) => {
    const headers = new Headers();

    await keycloak.updateToken(tokenExpirytime);

    if (typeof keycloak?.token !== 'string') {
      // console.log('Invalid Keycloak token detected.  ');
      return;
    }
    headers.set('keycloakToken', keycloak.token);

    // eslint-disable-next-line consistent-return
    return fetch(`${url}`, {
      headers,
      credentials: 'same-origin',
    });
  };

  const [patientState, setPatientState] = useState<PatientState>(null);
  const patientStateQueue: PatientState[] = [];
  const patientStateQueueRef = useRef(patientStateQueue);
  const patientStateRef = useRef(patientState);
  const [doPost, setDoPost] = useState<boolean>(false);
  const doPostRef = useRef(doPost);
  const [queueCount, setQueueCount] = useState(0);

  useEffect(() => {
    if (patientStateQueueRef.current.length > 0) {
      const diffPtObj = (pt: PatientState, ptState: PatientState) => {
        if (!pt) return;
        if (!ptState) {
          patientStateRef.current = pt;
          doPostRef.current = true;
          updateFormsPatientState && updateFormsPatientState(pt);
        } else if (ptState) {
          // if pt.ts is newer then patientState.ts, setptState
          const incommingTime = new Date(pt.ts).getTime();
          const currentTime = new Date(ptState.ts).getTime();
          const delta = incommingTime - currentTime;
          if (delta > 0) {
            patientStateRef.current = pt;
            doPostRef.current = true;
            updateFormsPatientState && updateFormsPatientState({ ...pt });
          }
        }
      };

      const ptObj = patientStateQueueRef.current.shift();
      diffPtObj(ptObj, patientStateRef.current);
      setQueueCount(queueCount - 1);
    }
  }, [queueCount, patientState]);

  // need a way to register a save event when a new change is made.
  // this needs to be set interval and not set timeout.  set timeout happen each time a change is registered.
  useEffect(() => {
    const interval = setInterval(() => {
      if (
        patientStateRef.current &&
        patientStateRef.current.ptObj &&
        doPostRef.current
      ) {
        doPostRef.current = false;
        keycloak
          .updateToken(tokenExpirytime)
          .success(() => {
            if (patientStateRef.current?.ptObj) {
              database
                ?.saveToPouch(
                  patientStateRef.current?.ptObj._id,
                  patientStateRef.current?.ptObj,
                )
                .then(() => {
                  if (database?.getErrorStatus()) {
                    setShowForms && setShowForms(false);
                    setShowFormslocal(false);
                    doPostRef.current = true;
                  }
                });
            }
          })
          .error((e) => {
            console.log(
              'There was an error updating the keycloak token.  So save to Pouch was done. ',
              e,
            );
          });
      }
    }, 20000);
    // cleanup the interval when component is unmounted.
    return () => clearInterval(interval);
  }, []);

  const updatePtStateObjRef = (ptStateObj: PatientState) => {
    if (ptStateObj) {
      patientStateQueueRef.current.push(ptStateObj);
      setQueueCount(patientStateQueueRef.current.length);
    }
  };

  const getFormattedArrivalDateTimeFromDate = (
    date: string | Date | null | undefined,
  ): string | undefined => {
    if (!date) {
      return undefined;
    }
    let dateString: string;
    /**
     * TODO Refactor once appvisit is more consistent.
     * 1) effective dates are sometimes still in object form instead of string.
     * 2) there is not a current way to get the system derived start time in appVisit, only user entered.
     */
    if (typeof date === 'object') {
      const tmpDate = date as Date;
      dateString = tmpDate.toISOString();
    } else if (typeof date === 'string') {
      dateString = date;
    } else {
      return undefined;
    }
    const formattedDateTime = moment
      .utc(dateString)
      .local()
      .format('MM/DD/YYYY hh:mm a');

    return formattedDateTime;
  };

  const getGmtOffset = function () {
    const today = new Date();
    const o = today.getTimezoneOffset() / 60;
    const sign = o <= 0 ? '+' : '-';
    return sign + o;
  };

  const generateC2VisitId = function () {
    const date = new Date();
    const visitTime = date.getTime();
    // NOTE: To ensure some sort of randomness, add a 6 digit random number
    const random = Math.round(1000000 * Math.random());
    return visitTime + random;
  };

  // testing method that will list the ids of the forms in the patient object from couch/pouch
  const getOpenFormIds = (ptObj: any) => {
    const forms: Array<string> = [];
    if (ptObj.questionnaires) {
      ptObj.questionnaires.forEach((q: any) => {
        forms.push(`${q.questionnaire['@id']}`);
      });
    }
    return forms;
  };

  /**
   * getInstanceId
   * @desc Returns a questionnaire instance ID. Remember the months are 0-11
   * @returns {string|*}
   */
  const getInstanceId = () => {
    const _now = moment.utc();
    const year = _now.format('YYYY');
    const month = parseInt(_now.format('MM')) - 1;
    const theRest = _now.format('DD-HH-s-SSS');
    return `${year}-${month}-${theRest}`;
  };

  const getUTCTS = () => moment.utc().format('YYYY-MM-DD-HH-mm-ss');

  const makeLocalStartDateTimeStr = (dt: string) => {
    if (!dt) {
      return undefined;
    }
    const reformattedDt = dt.replace(
      /(.*-.*-.*)-(.*)-(.*)-(.*)/,
      '$1 $2:$3:$4',
    );
    // format the date time to [MM/DD/YYYY hh:mm a] for non-military time.
    const formattedDate = moment
      .utc(reformattedDt)
      .local()
      .format('MM/DD/YYYY hh:mm a');

    return formattedDate;
  };

  type IndexedEpisode = { index: number; episode: mumms.episode } | undefined;
  const getCurrentEpisode = (): mumms.episode | undefined => {
    let episode: mumms.episode | undefined;
    if (patient && patient.episodes) {
      const { episodes } = patient;
      let newestEpisode: IndexedEpisode;

      episodes.forEach((epi, index) => {
        if (!newestEpisode) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          newestEpisode = {
            index,
            episode: epi,
          } as IndexedEpisode;
        } else {
          const neReferralDate = new Date(epi.referralDate).getTime();
          const curRefferralDate = new Date(
            newestEpisode.episode.referralDate,
          ).getTime();
          const delta = neReferralDate - curRefferralDate;
          if (delta > 0) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            newestEpisode = {
              index,
              episode: epi,
            } as IndexedEpisode;
          }
        }
        episode = newestEpisode?.episode;
      });
    }
    return episode;
  };

  const generateC2QuestionnaireURL = (instanceId: string) => {
    if (patient) {
      return `/m2/questionnaireEngine/${firm}/${location}/${
        patient?.patientNumber
      }/?instance-id=${instanceId}&id=${formName.split(':')[1]}`;
    }
    return undefined;
  };

  const createC2QuestionnaireObj = (
    ptState: PatientState,
  ): ClinicalQuestionnaire | undefined => {
    if (appVisit && ptState && ptState.ptObj && patient) {
      const instanceId = getInstanceId();
      return {
        questionnaire: {
          '@created-on': patient?.patientNumber,
          '@valid': true,
          '@valid-to-resolve': false,
          '@worker-name': `${appVisit.personfirstname} ${appVisit.personlastname}`,
          '@type': appVisit.visittype,
          '@global-id': '', // Not needed.
          '@url': generateC2QuestionnaireURL(instanceId) || '',
          '@instance-id': instanceId,
          '@label': null,
          '@start': new Date().toISOString(),
          '@resolved-on': '', // QE forms are not resolvable.
          '@visit-id': ptState?.ptObj.visits.visit['@id'].toString(),
          '@id': formName.split(':')[1],
          '@meds-explanation': '', // Meds are not used for QE
          '@name': '', // Name is pulled from the xml template clinical side.
          '@as-of': getUTCTS(),
          '@last-edited-on': '', // There are no editable forms in QE
          '@idgMeeting': '', // Not needed for QE
          reassess: false, // No reassessments are done in QE
          bulk: false, // No bulf forms are done in QE
          unique: false, // As of now, no unique forms.
          'response-givens': [],
        },
      };
    }
    return undefined;
  };

  const createC2VisitObj = () => {
    let ptObj;
    const currentEpisode = getCurrentEpisode();
    if (appVisit && patient && currentEpisode) {
      const formattedArrivalDateTime = getFormattedArrivalDateTimeFromDate(
        appVisit.effectivedatetime,
      );

      // 1) generate a utc formatted startdate for now();
      const startedTS = getUTCTS();
      // 2) based on this date, get the local time version of this date.
      const startedDateTime = makeLocalStartDateTimeStr(startedTS);

      ptObj = {
        _id: patient.patientNumber, // needed for pouch
        _rev: '', // needed for pouch
        ctmVisitId: appVisit.id,
        '@id': patient.patientNumber, // needed for hbc server
        firm,
        location,
        'first-name': patient.firstName,
        'last-name': patient.lastName,
        'admit-date': currentEpisode.admitDate,
        'referral-date': currentEpisode.referralDate,
        'discharge-date': currentEpisode.dischargeDate,
        status: patient.status,
        visits: {
          visit: {
            '@id': generateC2VisitId(),
            '@started': startedTS, // TODO update this to the visit start date when appVisit start tracking it.
            '@patient-id': patient.patientNumber,
            'started-date-time': {
              $t: startedDateTime,
            },
            'gmt-offset': {
              $t: getGmtOffset(),
            },
            'arrival-date-time': {
              $t: formattedArrivalDateTime,
            },
            'visit-type': {
              $t: '76',
              '@name': appVisit.visittype,
            },
            'arrival-coords': {
              '@lat': '',
              '@lng': '',
              '@acc': '',
            },
            '@closed': '',
            'depart-coords': {
              '@lat': '',
              '@lng': '',
              '@acc': '',
            },
          },
        },
        questionnaires: [],
      };
    }
    return ptObj;
  };

  const setError = (message: string) => {
    setQEErrorState({
      isError: true,
      information: message,
    });
    setShowForms && setShowForms(false);
    setShowFormslocal(false);
  };

  /**
   * This function checks the incoming patient state to make the correct visit and form information
   * is present in the patient state object.
   * If it finds bad visit information:  It remakes the visit from the current appVisit obj.
   * if it find a missing form, it adds that questionnaire object to the questionnaire array in ptState.
   *
   * If the passed in ptState is valid and is of the same visit as the current appVisit,
   * it returns the original passed in pt state.
   * @param ptState
   * @returns
   */
  const c2VisitVerification = (
    ptState: PatientState,
  ): PatientState | undefined => {
    let returnState;
    if (!appVisit) {
      // TODO set this error message to a constant
      setError(
        'There was an issue creating the clinical visit object. No CTM visit was opened at creation time.   Please refresh the page and open a new visit and try again. ',
      );
      return undefined;
    }
    // check various failure points.  If these points are hit, create a new visit object.
    if (
      !ptState ||
      !ptState.ptObj ||
      appVisit.id !== ptState.ptObj.ctmVisitId ||
      !ptState.ptObj['@id'] ||
      !ptState.ptObj.visits.visit
    ) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      // TODO ERROR TYPE as PatientState
      returnState = {
        ts: new Date(),
        ptObj: createC2VisitObj(),
      } as any;
      const newQuestionnaire = createC2QuestionnaireObj(returnState);
      if (newQuestionnaire) {
        returnState?.ptObj?.questionnaires?.push(newQuestionnaire);
      }
    } else {
      // otherwise, if there is a good patient state object that matches the current visit,
      // lets make sure its has the currently opened form.
      const inProgQuestionnaires = getOpenFormIds(ptState.ptObj);

      // check if the current opened form is in the ptState.  It it does not include the
      // current form, procede to make a new questionnaire obj
      if (!inProgQuestionnaires.includes(formName.split(':')[1])) {
        const newQuestionnaire = createC2QuestionnaireObj(ptState);
        returnState = { ...ptState };

        if (
          newQuestionnaire &&
          returnState &&
          returnState.ptObj &&
          returnState.ptObj.questionnaires
        ) {
          returnState?.ptObj?.questionnaires.push(newQuestionnaire);
        }
      }
    }

    return returnState || ptState;
  };
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  /**
   * When forms loads, register an event that notifies the user to close the form before
   * closing the tab or browser, or navigating away from the page.
   */
  useEffect(() => {
    const beforeUnloadFunction = (e: {
      preventDefault: () => void;
      returnValue: string;
    }) => {
      e.preventDefault();
      setIsNavigating(true);
      // the string returned here does not display. No need to translate it.  There
      // just has to be something returned in order for chrome to show the default message.
      e.returnValue = DEFAULT_STRING;
      return e.returnValue;
    };

    window.addEventListener('beforeunload', beforeUnloadFunction);

    return () =>
      window.removeEventListener('beforeunload', beforeUnloadFunction);
  }, []);

  React.useEffect(() => {
    const handleMessageListener = async (event: any) => {
      if (typeof event.data === 'object' && event.data['submission-started']) {
        setHideCheckmark && setHideCheckmark(true);
      } else if (
        typeof event.data === 'object' &&
        event.data['questionnaire-engine-manifest-qfile']
      ) {
        /**
         * If the event has questionnaire-engine-manifest-qfile:
         *  Push the new Questionnaire metadata to observation.
         *  Next, check if the pushed questionnaire can be removed from couch/pouch
         * Else
         *  Clinical iframe is requesting the patient object from C/P so that it can initialize
         */
        event.stopImmediatePropagation();
        event.stopPropagation();
        event.preventDefault();
        const { data } = event;
        const tz = moment.tz.guess();
        const siteTimezone = (currentUser && currentUser.siteTimezone) || tz;
        const date = formatPostDate(Date.now());
        const patientvisitId = appVisit ? appVisit.id : null;
        const visitdatetime = appVisit
          ? formatPostDate(appVisit.effectivedatetime)
          : null;
        const submissionForm = {
          id: '',
          createdatetime: date,
          createpersonid: currentUser?.userId?.toString() as string,
          createpersonnumber: currentUser?.personNumber as string,
          modifieddatetime: date,
          modifiedpersonid: currentUser?.userId?.toString() as string,
          modifiedpersonnumber: currentUser?.personNumber as string,
          formname: data.formname as string,
          manifesttype: 'Questionnaire',
          patientnumber: patient?.patientNumber as string,
          siteid: currentUser?.siteId.toString() as string,
          source: questionnaireEngine,
          sourcekey: data.sourcekey as string,
          timezone: siteTimezone as string,
          url: data.url as string,
          urltype: 'clinical_qfile',
          patientvisit_id: patientvisitId,
          responsedata: data.responsesBlob,
          visitdatetime,
          originalvisitdatetime: visitdatetime,
          originalsourcekey: data.sourcekey as string,
          isresolved: false,
          role_id: appVisit && providerInfo ? providerInfo.role_id : null,
        };
        observation
          .saveManifest(submissionForm)
          .then(async (res: any) => {
            if (!(res instanceof Response)) {
              dispatch({
                type: ADD_FORM,
              });
              // grab the couch/pouch copy of the patient record.
              const pat = (await database?.retrieve(
                patient?.patientNumber,
              )) as any;

              // remove the current questionnaire if it exists in c/p and resync c/p
              const copy = pat?.questionnaires;

              copy?.forEach((q: any, i: number) => {
                if (q.questionnaire['@instance-id'] === data.sourcekey) {
                  pat.questionnaires.splice(i, 1);
                }
              });

              const update = {
                ts: new Date(),
                ptObj: pat as PouchDB.Core.IdMeta &
                  PouchDB.Core.GetMeta &
                  ClinicalPatientObject,
              };
              patientStateQueueRef.current.push(update);
              setQueueCount(patientStateQueueRef.current.length);
              setHideCheckmark && setHideCheckmark(false); // so they can see the preview when it displays.
            } else {
              console.log(
                'There was an issue with saving the visit to observation.  Please refresh the page.  The following was returned:',
                res,
              );
              setHideCheckmark && setHideCheckmark(false); // give user back controle of the checkmark so they can close the widget.
            }
          })
          .catch((e: Error) => {
            // eslint-disable-next-line no-console
            console.error(e);
          });
      } else if (typeof event.data === 'object' && event.data.patientLoad) {
        /**
         * The clinical iframe is asking for the initial patient obj from C/P.  Get that object
         * and post a message for the clinical iframe with the the c/p returned patient object.
         */
        event.stopImmediatePropagation();
        event.stopPropagation();
        event.preventDefault();

        const pat = (await database?.retrieve(patient?.patientNumber)) as
          | void
          | (PouchDB.Core.IdMeta & PouchDB.Core.GetMeta & ClinicalPatientObject)
          | undefined;

        const newPatStateObj = {
          ts: new Date(),
          ptObj: pat,
        };
        const updatedPtStateObj = c2VisitVerification(newPatStateObj);

        if (!updatedPtStateObj) {
          setError(
            'There was a problem getting information about your open visit.  Please refresh the browser and try to open the form once more. ',
          );
        }
        updatePtStateObjRef(updatedPtStateObj);
        const message = {
          message: 'patientObj',
          patient: updatedPtStateObj?.ptObj || null,
        };
        if (iframeWindowRef.current && iframeWindowRef.current.contentWindow) {
          iframeWindowRef.current.contentWindow.postMessage(message, '*');
        }
      } else if (typeof event.data?.patientObj === 'object') {
        /*
         * User has entered a new entry into the form.  Iframe has sent us the updated patientObj.
         * Add a timestamp and add this new patientObj to the queue to be processed.
         */

        event.stopImmediatePropagation();
        event.stopPropagation();
        event.preventDefault();

        const { data } = event;
        const { patientObj, id } = data;
        // _id is needed for type match later in the save process.
        if (!patientObj._id) {
          patientObj._id = patientObj['@id'];
        }
        const update = {
          ts: new Date(),
          ptObj: patientObj as PouchDB.Core.IdMeta &
            PouchDB.Core.GetMeta &
            ClinicalPatientObject,
        };
        patientStateQueueRef.current.push(update);
        setQueueCount(patientStateQueueRef.current.length);
      }
    };
    window.addEventListener('message', handleMessageListener);

    // cleanup this component
    return () => {
      window.removeEventListener('message', handleMessageListener);
    };
  }, []); // keep this empty, so the listener is only established on widget load.

  useEffect(() => {
    if (!engineStarted) {
      startEngine(true);
      localStorage.setItem('token', keycloak.token as string);
      const qeURL = `${
        process.env.REACT_APP_HBC_BASE_URL
      }/m2/questionnaireEngine/${firm}/${location}/${
        patient?.patientNumber
      }?id=${formName.split(':')[1]}&visitID=&sticky=${
        formName.split(':')[2]
      }&loggedInWithSSO=true`;
      const fetchQuestionnaireEngine = async (src: string) => {
        let qhString = '';
        const getHtml = async () => {
          let res = (await attemptFetch(src)
            .then((data) => data)
            .catch((error) => {
              console.error('e', error);
              return undefined;
            })) as Response;
          if (res === undefined) {
            if (onClick) {
              onClick();
            } // this is prop passed from dashboard/sidebar/FormListMenu
            return '';
          }
          if (res.status === 403) {
            if (onClick) {
              onClick();
            }
            res = (await attemptFetch(src)) as Response;
          }
          if (!res.ok) {
            return '';
          }
          return res.text();
        };

        qhString = await getHtml();

        if (qhString !== '') {
          qhString = qhString
            .replaceAll('src="/', `src="${process.env.REACT_APP_HBC_BASE_URL}/`)
            .replaceAll(
              'href="/',
              `href="${process.env.REACT_APP_HBC_BASE_URL}/`,
            );
          setQuestionnaireHtml(qhString);
        }
      };

      fetchQuestionnaireEngine(qeURL);
    }
  }, [engineStarted, keycloak.token, firm, location, patient, formName]);

  /**
   * The clinical iframe cannot refresh the token itself, nor can we pass the keycloak
   * object directly to the iframe.
   * See README at top of page.
   */
  useEffect(() => {
    keycloak.onAuthRefreshSuccess = () => {
      if (iframeWindowRef.current && iframeWindowRef.current.contentWindow) {
        (async () => {
          await keycloak.updateToken(tokenExpirytime);
        })();

        const message = {
          keycloakToken: keycloak.token,
        };
        iframeWindowRef.current.contentWindow.postMessage(message, '*');
      }
    };
  }, [keycloak, tokenExpirytime]);

  const displayErrorInformation = () => (
    <ul>
      {Object.entries(cpErrMsgRef.current).map((cur) => (
        <li key={`error-item-${cur[0]}`}>{`${cur[0]}: ${cur[1]}`}</li>
      ))}
    </ul>
  );
  type QEErrorState = {
    isError: boolean;
    information: string;
  };
  const [qeErrorState, setQEErrorState] = useState<QEErrorState>({
    isError: false,
    information: '',
  });

  const errorMessageDisplayRef = useRef(displayErrorInformation());
  const [errorMessageElement, setErrorMessageElement] = useState(
    displayErrorInformation(),
  );
  /*
    If there is an error status thrown in couch/pouch.  We want to remove the form from the 
    user before they have a chance to edit any further data. Display an error message asking
    the user to refresh the screen. 
  */
  useEffect(() => {
    if (database?.getErrorStatus()) {
      const data = {
        patientNumber: patient?.patientNumber,
        formName: formName.split(':')[1],
        firm,
        location,
        personNumber: currentUser.personNumber,
        usersName: `${currentUser.firstName} ${currentUser.lastName}`,
        errorDate: new Date(),
        errorMessage: database?.getErrorMessage() || '',
      };
      cpErrMsgRef.current = data;
      setCouchPouchErrorMsg(data);
      errorMessageDisplayRef.current = displayErrorInformation();
      setErrorMessageElement(displayErrorInformation());
      setShowFormslocal(false);
      setShowForms && setShowForms(false);
    } else if (qeErrorState.isError) {
      const data = {
        patientNumber: patient?.patientNumber,
        formName: formName.split(':')[1],
        firm,
        location,
        personNumber: currentUser.personNumber,
        usersName: `${currentUser.firstName} ${currentUser.lastName}`,
        errorDate: new Date(),
        errorMessage: qeErrorState.information,
      };
      cpErrMsgRef.current = data;
      setCouchPouchErrorMsg(data);
      errorMessageDisplayRef.current = displayErrorInformation();
      setErrorMessageElement(displayErrorInformation());
      setShowFormslocal(false);
      setShowForms && setShowForms(false);
    }
  }, [
    setShowForms,
    showFormsLocal,
    showForms,
    database,
    patient,
    formName,
    firm,
    location,
    currentUser.personNumber,
    currentUser.firstName,
    currentUser.lastName,
    qeErrorState.isError,
    qeErrorState.information,
  ]);

  const handleModleClose = () => {
    setIsNavigating(false);
  };

  /* Note:  We used two variables to determine if the forms iframe should be shown.  
    ShowFormsLocal will be set to false as soon as the error initially happens and will 
    cause the form to be removed when the user is actively working
    in the form.  ShowForms is defined in the Dashboard and passed down. This way there 
    state that is preserved in between opening different forms.  That way if there is an 
    error in a form, the user can not click on another form and document while there is a 
    db error. 
  */
  return (
    <FormWidgetWrapper>
      {isNavigating && showSaveWarningDialog(isNavigating, handleModleClose)}
      {!questionnaireHtml && <Loader />}
      {questionnaireHtml && showForms && showFormsLocal ? (
        <div>
          <iframe
            ref={(e) => {
              iframeWindow = e;
              iframeWindowRef.current = e;
            }}
            title="questionnaireEngine"
            srcDoc={questionnaireHtml}
            name={process.env.REACT_APP_HBC_BASE_URL}
            style={{
              width: '100%',
              height: height
                ? `${height}px`
                : topBarHeight
                ? `calc(100vh - ${topBarHeight}px)`
                : `80vh`,
              visibility: hideCheckmark || showSpinner ? 'hidden' : 'visible',
            }}
          />
          <div
            style={{
              visibility: hideCheckmark || showSpinner ? 'visible' : 'hidden',
            }}
          >
            {showSaveWarning()}
            <Loader />
          </div>
        </div>
      ) : (
        <div>
          <div>
            {showForms && showFormsLocal ? (
              <div>
                {showSaveWarning()}
                <Loader />
              </div>
            ) : (
              <div>
                <p>{translations.couchPouchErrorMsg}</p>
                {errorMessageElement}
              </div>
            )}
          </div>
        </div>
      )}
    </FormWidgetWrapper>
  );
};

export default QuestionnaireEngine;
